import { KeyboardLayout } from '../types/enums';
import { findExercise, stories, tracks } from '../data/staticCurriculum';

/**
 * Canonical route scheme (owner manual-check item 6). Generated links use
 * ONLY the short forms; every long /tracks/... path survives as a
 * compatibility redirect for existing bookmarks (see router/index.tsx).
 * Storage identities (trackKey/exerciseSlug keys, attempt ids, drafts,
 * archives) are independent of URLs and unchanged.
 *
 * Slug collision safety: exercise slugs are proven globally unique across
 * the catalog and none equals the reserved literal `game` (verified by
 * tests); story slugs are unique within their own namespace
 * (/lessons/stories/:storySlug), which cannot collide with lesson slugs.
 */

/** Hub sections addressable from a /lessons deep link (GH #124). */
export type HubSectionKey = 'stories' | 'game';

/** Query state the /lessons hub reacts to. One source for every hub link. */
export interface LessonsHubQuery {
  track?: string;
  section?: HubSectionKey;
  /** Exercise type filter: CHARACTER | WORD | SENTENCE. */
  type?: string;
  /** Progress filter: not-started | in-progress | completed. */
  status?: string;
  /** Opens the hub preferences/data panel (GH #126). */
  panel?: 'preferences';
}

export function getLessonsHubPath(query: LessonsHubQuery = {}): string {
  const params = new URLSearchParams();
  if (query.track) params.set('track', query.track);
  if (query.section) params.set('section', query.section);
  if (query.type) params.set('type', query.type);
  if (query.status) params.set('status', query.status);
  if (query.panel) params.set('panel', 'preferences');
  const search = params.toString();
  return search ? `/lessons?${search}` : '/lessons';
}

/** Canonical lesson practice: /lessons/:lessonSlug. */
export function getLessonPracticePath(exerciseSlug: string): string {
  return `/lessons/${encodeURIComponent(exerciseSlug)}`;
}

/** Canonical story practice: /lessons/stories/:storySlug. */
export function getStoryPracticePath(storySlug: string): string {
  return `/lessons/stories/${encodeURIComponent(storySlug)}`;
}

/** Canonical game: /lessons/game (the legacy /game redirects here). */
export function getGamePath(): string {
  return '/lessons/game';
}

/** Canonical result: /results/:attemptId (identity from the stored attempt). */
export function getResultPath(attemptId: string | number): string {
  return `/results/${encodeURIComponent(String(attemptId))}`;
}

// ---------------------------------------------------------------------------
// Legacy /tracks/... path builders — used by the compatibility redirects.
// Removal condition: retire only when the Human Owner explicitly drops
// external-bookmark compatibility for pre-short-URL links.
// ---------------------------------------------------------------------------

export function getTrackPath(trackKey: string): string {
  return `/tracks/${trackKey}`;
}

export function getExercisePath(trackKey: string, exerciseSlug: string): string {
  return `${getTrackPath(trackKey)}/exercises/${exerciseSlug}`;
}

export function getExercisePracticePath(trackKey: string, exerciseSlug: string): string {
  return `${getExercisePath(trackKey, exerciseSlug)}/practice`;
}

/** Legacy summary path; new links use /results/:attemptId. */
export function getExerciseSummaryPath(
  trackKey: string,
  exerciseSlug: string,
  attemptId?: string | number | null
): string {
  const basePath = `${getExercisePath(trackKey, exerciseSlug)}/summary`;
  if (attemptId === undefined || attemptId === null) return basePath;
  return `${basePath}/${encodeURIComponent(String(attemptId))}`;
}

/**
 * Resolve a canonical lesson slug to its (trackKey, exerciseSlug). Slugs are
 * globally unique across the catalog, so the first hit is the authority.
 */
export function resolveLessonSlug(
  lessonSlug: string,
): { trackKey: string; exerciseSlug: string } | null {
  for (const track of tracks) {
    if (findExercise(track.trackKey, lessonSlug)) {
      return { trackKey: track.trackKey, exerciseSlug: lessonSlug };
    }
  }
  return null;
}

/** Resolve a canonical story slug to its numeric id. */
export function resolveStorySlug(storySlug: string): number | null {
  const story = stories.find((candidate) => candidate.storySlug === storySlug);
  return story ? story.id : null;
}

export function getDefaultTrackKey(layout?: KeyboardLayout | string): string {
  const normalizedLayout = (layout ?? KeyboardLayout.INSCRIPT).toString().toLowerCase();
  return `hindi-${normalizedLayout}-beginner`;
}
